import { GenericLayout } from '@/components/layouts/GenericLayout/GenericLayout';
import { ErrorScreen } from '@/components/shared/ErrorScreen/ErrorScreen';
import { FullPageSpinner } from '@/components/shared/FullPageSpinner/FullPageSpinner';
import { GenericFormScreen } from '@/components/shared/GenericFormScreen/GenericFormScreen';
import { GenericIconScreen } from '@/components/shared/GenericIconScreen/GenericIconScreen';
import { LinkExpiredScreen } from '@/components/shared/LinkExpiredScreen/LinkExpiredScreen';
import { Button } from '@/components/ui/Button/Button';
import { paths } from '@/config/paths';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { BadgeHelp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useGetForgottenUsernameList } from '../../api/getForgottenUsernameList';
import { UsernameListItem } from './components/UsernameListItem/UsernameListItem';

interface ForgotUsernameListProps {
  token: string;
}

export const ForgotUsernameList = ({ token }: ForgotUsernameListProps) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'forgotUsernameList',
  });

  const { translateError } = useErrorTranslation();

  const navigate = useNavigate();

  const { data, error, isLoading, isError } = useGetForgottenUsernameList({
    token: token,
  });

  if (isLoading) {
    return <FullPageSpinner />;
  }

  const isTokenExpired = !!error?.errors?.some((error) => error.code === 'FORGOTTEN_USERNAME_TOKEN_EXPIRED');

  if (isTokenExpired) {
    return <LinkExpiredScreen />;
  }

  const isTokenInvalid = !!error?.errors?.some((error) => error.code === 'FORGOTTEN_USERNAME_TOKEN_INVALID');

  if (isTokenInvalid) {
    return <ErrorScreen description={translateError(error)} withSeoTitle />;
  }

  if (isError) {
    return <ErrorScreen withSeoTitle />;
  }

  const usernames = data?.data?.usernames ?? [];

  if (usernames.length === 0) {
    return (
      <GenericLayout>
        <GenericIconScreen
          Icon={BadgeHelp}
          title={t('empty.title')}
          description={t('empty.description')}
          leftActionElement={
            <Button asChild>
              <Link to={paths.auth.login.path}>{t('button.label')}</Link>
            </Button>
          }
        />
      </GenericLayout>
    );
  }

  return (
    <GenericLayout>
      <GenericFormScreen
        title={t('title')}
        description={t('description')}
        leftActionElement={<Button onClick={() => void navigate(paths.auth.login.path)}>{t('button.label')}</Button>}
        contentClassName='mb-0 tablet:mb-0'
      >
        <ul>
          {usernames.map((item) => (
            <li key={item.username} className='border-outline-input-default mb-8 border-b pb-8 last:mb-0 last:border-b-0 last:pb-0'>
              <UsernameListItem username={item.username} center={item.center?.name ?? 'N/A'} />
            </li>
          ))}
        </ul>
      </GenericFormScreen>
    </GenericLayout>
  );
};
